// ================= IMPORTS =================
import React, { useState, useEffect, useRef } from "react";
import Main from "../Main";
import LpgServicesForm from "./LpgServiceForm";
import { Autocomplete, TextField } from "@mui/material";

import {
  fetch_lpg_info,
  fetch_lpg_operators,
  submit_lpg_amount,
} from "../../api/billPayApi";

import { getLocation } from "../../components/Navigator/Navigator";
import { useParams, useSearchParams } from "react-router-dom";
import { useInactivityTimeout } from "../../hooks/useInactivityTimeout";
import { useNotify } from "../../context/NotifyContext";
import { setLocation } from "../../features/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppBreadcrumbs } from "../../components/listing";

// ================= COMPONENT =================
const LpgBooking = () => {
  const { notify } = useNotify();
  const { slug } = useParams();
  const dispatch = useDispatch();
  const { location: savedLocation } = useSelector((state) => state.auth);

  const [searchParams, setSearchParams] = useSearchParams();

  const [consumerNumber, setConsumerNumber] = useState("");
  const [ad2, setAd2] = useState(""); // Registered Mobile / LPG ID
  const [adFields, setAdFields] = useState({ ad1: "2" }); // default mobile

  const [details, setDetails] = useState(null);
  const [operator, setOperator] = useState(null);
  const [operators, setOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [selectedOperatorId, setSelectedOperatorId] = useState("");

  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchedRef = useRef(null);

  // ================= CLEAR SESSION =================
  const clearSession = () => {
    fetchedRef.current = null;
    setSearchParams({}, { replace: true });
    setConsumerNumber("");
    setAd2("");
    setDetails(null);
    setOperator(null);
    setShowDetails(false);
    setAdFields({ ad1: "2" });
  };

  // ================= INACTIVITY =================
  useInactivityTimeout(() => {
    if (showDetails) {
      clearSession();
      notify("Session expired", "info");
    }
  }, 10 * 60 * 1000);

  // ================= LOAD OPERATORS =================
  useEffect(() => {
    const load = async () => {
      const res = await fetch_lpg_operators();
      if (res?.success) setOperators(res.data?.operators || []);
    };
    load();
  }, []);

  // ================= AUTO SELECT OPERATOR =================
  useEffect(() => {
    if (selectedOperatorId && operators.length) {
      const op = operators.find((o) => o.operatorId === selectedOperatorId);
      setSelectedOperator(op);
    }
  }, [selectedOperatorId, operators]);

  // ================= INPUT HANDLERS =================
  const handleConsumerChange = (e) => {
    setConsumerNumber(e.target.value);
  };

  const handleAd2Change = (e) => {
    const value = e.target.value;
    setAd2(value);

    // auto detect LPG ID / Mobile
    if (value.length > 10) {
      setAdFields({ ad1: "1" }); // LPG ID
    } else {
      setAdFields({ ad1: "2" }); // Mobile
    }
  };

  // ================= SUBMIT =================
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedOperatorId) return notify("Select LPG operator", "error");
    if (!consumerNumber) return notify("Consumer Number required", "error");
    if (!ad2) return notify("Registered Mobile / LPG ID required", "error");

    setSearchParams({
      canumber: consumerNumber,
      operator: selectedOperatorId,
    });
  };

  // ================= FETCH BILL =================
  const fetchBill = async (canumber, opId) => {
    if (loading) return;
    setLoading(true);

    try {
      const payload = {
        mode: "online",
        canumber,
        operatorId: opId,
        ad1: adFields.ad1 || "2",
        ad2: ad2,
      };

      const result = await fetch_lpg_info(payload);

      if (result?.success && result?.data?.bill) {
        notify("Bill fetched successfully", "success");
        setDetails(result.data.bill);
        setOperator(result.data.operator);
        setShowDetails(true);
      } else {
        notify(result?.message || "Bill not found", "error");
        // clearSession();
      }
    } catch (err) {
      console.error(err);
      notify("Server error", "error");
      clearSession();
    } finally {
      setLoading(false);
    }
  };

  // ================= URL WATCH =================
  useEffect(() => {
    const canumber = searchParams.get("canumber");
    const opId = searchParams.get("operator");

    if (!canumber || !opId) return;
    if (fetchedRef.current === canumber) return;

    fetchedRef.current = canumber;
    setConsumerNumber(canumber);
    setSelectedOperatorId(opId);

    setTimeout(() => fetchBill(canumber, opId), 200);
  }, [searchParams]);

  // ================= PAY BILL =================
  const handlePay = async (data) => {
    if (!details || !consumerNumber) return notify("Missing fields", "error");
    if (!data.amount) return notify("Amount required", "error");

    setLoading(true);

    try {
      let location = savedLocation;
      if (!location) {
        location = await getLocation();
        dispatch(setLocation(location));
      }

      const payload = {
        amount: data.amount,
        operatorId: selectedOperatorId,
        canumber: consumerNumber,
        latitude: location.latitude,
        longitude: location.longitude,
        category: slug,
          ad1: adFields.ad1 || "2",
        ad2: ad2,
      };

      const result = await submit_lpg_amount(payload);

      if (result?.success) {
        notify("Payment Successful", "success");
        clearSession();
      } else {
        notify(result?.message || "Payment failed", "error");
      }
    } catch (err) {
      console.error(err);
      notify("Server error", "error");
    } finally {
      setLoading(false);
    }
  };

  // ================= UI =================
  return (
    <Main showRightPanel={false}>
      <AppBreadcrumbs
        items={[
          { label: "Dashboard", to: "/" },
          { label: "Recharge & Bills" },
          { label: "LPG Bill" },
        ]}
      />

      <div className="flex flex-col lg:flex-row gap-4 mt-4">
        <div className="ds-card p-3 sm:p-4 w-full max-w-[280px]">
          <h2 className="text-[14px] font-semibold mb-4 text-[var(--text-dark)]">
            LPG Bill
          </h2>

          <form onSubmit={handleSubmit} className="space-y-3">
            <label className="ds-label">Select LPG Operator</label>
            <Autocomplete
              options={operators}
              getOptionLabel={(op) => op.name || ""}
              value={selectedOperator}
              onChange={(e, val) => {
                setSelectedOperator(val);
                setSelectedOperatorId(val?.operatorId || "");
              }}
              renderInput={(params) => (
                <TextField {...params} size="small" placeholder="Search Operator" />
              )}
            />

            <label className="ds-label">Consumer Number *</label>
            <TextField
              fullWidth
              size="small"
              value={consumerNumber}
              onChange={handleConsumerChange}
              placeholder="Consumer Number"
            />

            <label className="ds-label">
              {selectedOperator?.displayname || "Registered Mobile / LPG ID"} *
            </label>
            <TextField
              fullWidth
              size="small"
              value={ad2}
              onChange={handleAd2Change}
              placeholder="Enter Mobile / LPG ID"
            />

            <button
              type="submit"
              disabled={loading}
              className="ds-btn ds-btn-primary ds-btn-block"
            >
              {loading ? "Fetching..." : "Check Bill"}
            </button>
          </form>
        </div>

        {showDetails && details && (
          <div className="w-full max-w-sm">
            <LpgServicesForm
              bill={details}
              operator={operator}
              loading={loading}
              onSubmit={handlePay}
              close={clearSession}
            />
          </div>
        )}
      </div>
    </Main>
  );
};

export default LpgBooking;
